import { useCallback, useRef, useState } from "react";
import { importService } from "../services/importMockService";

export function useImportRoster() {
  const [step, setStep] = useState("UPLOAD");
  const [file, setFile] = useState(null);
  const [mappingResult, setMappingResult] = useState(null);
  const [importId, setImportId] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [importHistory, setImportHistory] = useState([]);

  const uploadFile = async (f) => {
    setFile(f);
    setStep("ANALYZING");
    setIsLoading(true);

    try {
      const uploadRes = await importService.uploadRoster(f);
      const id = uploadRes.import_id;
      setImportId(id);

      const mapping = await importService.validateRoster(id);
      setMappingResult(mapping);
      await getRostersHistory();
      setStep("MAPPING_REVIEW");
    } catch (err) {
      console.error("Upload/Validate failed:", err);
      setStep("UPLOAD");
    } finally {
      setIsLoading(false);
    }
  };

  // startImport only triggers the API and moves to PROCESSING.
  // All progress tracking lives in StepProcessing via polling.
  const startImport = async ({ file, mapping }) => {
    try {
      await importService.startRosterImport({ importId, mapping });
    } catch (err) {
      console.error("Start import failed:", err);
    }
    setStep("PROCESSING");
  };

  const deleteImport = async () => {
    if (!importId) return;
    try {
      setIsLoading(true);
      await importService.deleteRoster(importId);
      await getRostersHistory();
      reset();
    } catch (err) {
      console.error("Delete import failed:", err);
    } finally {
      setIsLoading(false);
    }
  };

  const pendingHistoryFetch = useRef(null);

  const getRostersHistory = useCallback(async () => {
    if (pendingHistoryFetch.current) return pendingHistoryFetch.current;

    setHistoryLoading(true);
    const promise = importService
      .getImportRostersHistory()
      .then((res) => {
        setImportHistory(res || []);
      })
      .catch((err) => {
        console.error("Fetch import history failed:", err);
      })
      .finally(() => {
        setHistoryLoading(false);
        pendingHistoryFetch.current = null;
      });

    pendingHistoryFetch.current = promise;
    return promise;
  }, []);

  const getRosterImportResults = useCallback(async (import_id) => {
    try {
      const res = await importService.getRosterImportStatus(import_id);
      return res;
    } catch (err) {
      console.error("Fetch roster import results failed:", err);
      return null;
    }
  }, []);

  const deleteHistoryItem = async (import_id) => {
    try {
      const res = await importService.deleteRoster(import_id);
      if (res?.deleted) {
        setImportHistory((prev) =>
          prev.filter((item) => item.import_id !== res.id),
        );
        return true;
      }
      return false;
    } catch (err) {
      console.error("Delete roster failed:", err);
      return false;
    }
  };

  const updateHistoryItem = useCallback((importId, updates) => {
    setImportHistory((prev) =>
      prev.map((item) =>
        item.import_id === importId ? { ...item, ...updates } : item,
      ),
    );
  }, []);

  const reset = () => {
    setStep("UPLOAD");
    setFile(null);
    setMappingResult(null);
    setImportId(null);
  };

  const goBack = () => setStep("UPLOAD");
  const goToUpload = () => reset();

  return {
    step,
    file,
    mappingResult,
    importId,
    isLoading,
    uploadFile,
    startImport,
    deleteImport,
    reset,
    goBack,
    goToUpload,
    getRostersHistory,
    importHistory,
    historyLoading,
    getRosterImportResults,
    setImportId,
    setStep,
    setMappingResult,
    deleteHistoryItem,
    updateHistoryItem,
  };
}
